<template>
  <transition name="fade">
    <div
      v-if="showUpsell"
      v-scroll-lock="showUpsell"
      role="dialog"
      aria-labelledby="upsellHeadline"
      aria-modal="true"
      class="product-upsell"
    >
      <button
        @click="dismiss"
        class="product-upsell__close-button"
        aria-label="close dialog"
      >
        <icon name="close" :size="$mq === 'mobile' ? '18px' : '21px'"/>
      </button>
      <div class="product-upsell__inner">
        <div class="product-upsell__headline">
          <h4 :class="$mq === 'mobile' ? 'h7' : 'h6'" id="upsellHeadline">{{ modalContent.headline }}</h4>
        </div>
        <div class="product-upsell__content">
          <div class="product-upsell__content-wrapper">
            <figure class="product-upsell__figure">
              <picture class="product-upsell__picture">
                <source media="(min-width: 650px)" :srcset="`${imageSize(product.images[0].src, '260x')}, ${imageSize(product.images[0].src, '520x')} 2x`">
                <source media="(min-width: 300px)" :srcset="`${imageSize(product.images[0].src, '150x')}, ${imageSize(product.images[0].src, '300x')} 2x`">
                <img
                  class="product-upsell__image"
                  :src="product.images[0].src"
                  :alt="product.images[0].alt"
                >
              </picture>
            </figure>
            <div class="product-upsell__details">
              <span class="product-upsell__vendor">
                {{ product.vendor }}
              </span>
              <div class="product-upsell__title" :class="$mq === 'mobile' ? 'h7' : 'h4'">
                {{ product.title }}
              </div>
              <div class="product-upsell__short-description" v-html="modalContent.short_description" />
              <span class="product-upsell__price h7">
                {{ product.selected_or_first_available_variant.price | moneyWithoutDecimals }}
              </span>
            </div>
            <div class="product-upsell__actions">
              <button class="button button--dark up-sell-atc ab_test_cta" @click="addToCart">
                <span class="button__content">{{ modalContent.add_to_cart_button_text }}</span>
              </button>
              <button class="button button--primary" @click="dismiss">
                <span class="button__content">{{ modalContent.close_upsell_button_text }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';
import Icon from '@/scripts/components/basic/Icon.vue';
import { imageSize } from '@/scripts/helpers/util';

export default {
  name: 'ProductUpsell',
  components: {
    Icon,
  },
  watch: {
    showUpsell: function(visible) {
      if (!visible) {
        window.removeEventListener('keyup', this.escape);
        this.$store.dispatch('fullOverlay/setVisibility', visible);
        return;
      }

      this.$store.dispatch('fullOverlay/setVisibility', visible);
      this.$store.dispatch('fullOverlay/setIndex', 111);
      window.addEventListener('keyup', this.escape);

      this.$nextTick(() => {
        this.setFocusElements();
      });
    },
  },
  computed: {
    ...mapState({
      type: ({ upsell }) => upsell.type,
      modalContent: ({ upsell }) => upsell.modalContent,
      showUpsell: ({ upsell }) => upsell.showUpsell,
      product: ({ upsell }) => upsell.modalContent.product,
    }),
  },
  data() {
    return {
      focusableElements: [],
    }
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.escape);
  },
  methods: {
    imageSize,
    addToCart() {
      this.$store.dispatch('upsell/addedUpsell');
    },
    dismiss() {
      this.$store.dispatch('upsell/closeUpsell');
    },
    /**
     * @desc assigns elements capable of being focused and sets focus to the first element.
     *     Adds a listener to the blur event of the last focusable element within the upsell that
     *     sets focus back to the first focusable element in the modal, creating a loop of all
     *     focusable modal elements.
     */
    setFocusElements() {
      this.focusableElements = [
        ...this.$el.querySelectorAll(
          'input:not([type="hidden"]), a, select, button, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => !el.hasAttribute('disabled'));
      if (!this.focusableElements.length) return;
      
      this.focusableElements[0].focus();
      this.focusableElements[this.focusableElements.length - 1].addEventListener('blur', this.focusLoop);
    },
    focusLoop() {
      requestAnimationFrame(() => {
        this.focusableElements[0].focus();
      });
    },
    escape(e) {
      if (e.key === 'Escape') {
        this.dismiss();
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .product-upsell {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 100%;
    padding: rem(18);
    max-width: percentage(345 / 375);
    background: $color-white;
    transform: translate(-50%, -50%);
    z-index: 112;

    @include desktop-up {
      max-width: rem(780);
      padding: rem(21);
    }
  }

  .product-upsell__close-button {
    display: block;
    margin-left: auto;
  }

  .product-upsell__headline {
    max-width: percentage(271 / 345);
    margin: 0 auto rem(20);
    padding-top: rem(30);
    text-align: center;

    @include desktop-up {
      margin-bottom: rem(38);
      padding-top: rem(8);
    }
  }

  .product-upsell__content {
    margin-bottom: rem(42);

    @include desktop-up {
      margin-bottom: rem(59);
    }
  }

  .product-upsell__content-wrapper {
    display: grid;
    grid-template: auto / percentage(116 / 281) 1fr;
    column-gap: rem(24);
    row-gap: rem(30);
    margin-left: rem(4);
    margin-right: rem(-4);

    @include desktop-up {
      column-gap: rem(41);
      row-gap: rem(21);
      margin-left: rem(58);
      margin-right: rem(66);
    }
  }

  .product-upsell__figure {
    position: relative;
    padding-bottom: percentage(150 / 116);

    @include desktop-up {
      grid-row: 1 / 3;
      padding-bottom: percentage(358 / 277);
    }
  }

  .product-upsell__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .product-upsell__details {
    align-self: flex-end;
  }

  .product-upsell__vendor,
  .product-upsell__title {
    margin-bottom: rem(5);
  }

  .product-upsell__vendor,
  .product-upsell__short-description {
    color: $color-black-700;
  }

  .product-upsell__vendor {
    display: inline-block;
    color: $color-black-700;
    font-size: rem(12);
    line-height: (16 / 12);
    
    @include mobile-only {
      margin-bottom: rem(8);
    }
  }

  .product-upsell__title,
  .product-upsell__price {
    color: $color-black-900;
  }

  .product-upsell__short-description {
    margin-bottom: rem(12);
    font-size: rem(12);
    letter-spacing: rem(-0.1);

    @include desktop-up {
      font-size: rem(15);
      margin-bottom: rem(17);
    }
  }

  .product-upsell__price {
    @include mobile-only {
      font-size: rem(13);
      line-height: (15 / 13);
    }
  }  
  button.button.button--dark.up-sell-atc {
    &:hover {
      background: white;
      color: #1D1D20;
      border: 2px solid #1D1D20;
    }
    
  }

  .product-upsell__actions {
    display: flex;
    flex-direction: column;

    @include mobile-tablet {
      grid-column: 1 / 3;
    }

    .button:first-of-type {
      margin-bottom: rem(10);
    }
  }
</style>